import { LoadType, PointLoad } from 'types/domainTypes';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { EditDialog } from 'components/EditDialog/EditDialog';
import { Alert } from '@mui/material';
import { NumericTextField } from 'components/FormFields/NumericTextField';
import { ReactElement } from 'react';

export type PointLoadFormData = {
  pointLoad: number | '';
  location: number | '';
  id?: string;
};

interface PointLoadDialogProps {
  formData?: PointLoad;
  title: string;
  loadType: LoadType;
  onClose: () => void;
  onSave: (formData: PointLoadFormData) => void;
  maxLocationValue: number;
}

export function PointLoadDialog({ formData, title, loadType, onClose, onSave, maxLocationValue }: PointLoadDialogProps): ReactElement {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<PointLoadFormData>({
    reValidateMode: 'onChange',
    defaultValues: {
      pointLoad: formData?.load || '',
      location: formData?.position !== undefined ? formData?.position : '',
      id: formData?.id,
    },
  });

  const submitHandler = (submitData: PointLoadFormData) => {
    if (loadType === LoadType.SuctionLoad) {
      submitData.pointLoad = -Math.abs(submitData.pointLoad as number);
    }

    onSave(submitData);
  };

  return (
    <EditDialog
      title={title}
      onClose={onClose}
      handleFormSubmit={handleSubmit(submitHandler)}
      isDirty={isDirty}
      saveActionText={t('calculation-page.spans-and-loads-dialog.point-load-dialog.save-action-text')}
    >
      <Alert severity="info">{t('calculation-page.spans-and-loads-dialog.point-load-dialog.alert')}</Alert>
      {loadType === LoadType.PermanentLoad && (
        <Alert severity="info">{t('calculation-page.spans-and-loads-dialog.removable-load-alert')}</Alert>
      )}
      <NumericTextField
        data-testid={'point-load-location-test-id'}
        name="location"
        required
        control={control}
        label={t('calculation-page.spans-and-loads-dialog.point-load-dialog.label-location')}
        fullWidth
        maxValue={{
          value: maxLocationValue,
          errorText: t('calculation-page.spans-and-loads-dialog.point-load-dialog.max-value-error', { maxLocation: maxLocationValue }),
        }}
        adornmentProps={{ endAdornment: 'm' }}
        decimalPlaces={3}
        allowZero
      />
      <NumericTextField
        data-testid={'point-load-test-id'}
        name="pointLoad"
        required
        control={control}
        label={t('calculation-page.spans-and-loads-dialog.point-load-dialog.label-load')}
        fullWidth
        adornmentProps={{ endAdornment: 'kN' }}
        decimalPlaces={3}
        requiredTextAdornment={
          loadType === LoadType.SuctionLoad
            ? t('calculation-page.spans-and-loads-dialog.point-load-dialog.required-text-adornment')
            : undefined
        }
        allowNegative={loadType === LoadType.SuctionLoad}
      />
    </EditDialog>
  );
}
